// client/src/components/pages/LexiStar/RecomputeJobs/RecomputeJobs.jsx
//
// LexiStar recompute jobs (ST-00188, M3-U1; T4-S12; LS-09 §5.19). Routes
// /lexistar/recompute-jobs (list) and /lexistar/recompute-jobs/:id (one job
// with its diff — RecomputeJobDetail). Page permission lexistar.recompute.run;
// publishing needs lexistar.recompute.signoff and a user other than the one
// who asked (SR-21, enforced by the database).
//
// Recomputes normally start from the object that changed (RecomputeOffer in
// the geofence and route editors, or a past-dated declaration); "New
// recompute…" here starts a manual one. The list polls while any job is
// computing or publishing, and refetches on `lexistar:recompute`.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaHistory, FaPlus, FaSyncAlt } from 'react-icons/fa';
import SegmentedControl from '../../../common/SegmentedControl';
import { useAuth } from '../../../../context/AuthContext';
import { listRecomputeJobs, pickList } from '../../../../services/lexistarAssurance';
import {
  PageHeader,
  ErrorBanner,
  LoadingState,
  EmptyState,
  NoPermissionPage,
  ConnectionIndicator,
  cardClasses,
} from '../StatusBoard/atoms';
import { fmtDateTime } from '../StatusBoard/format';
import { ProgressBar, useAssuranceEvents, buttonClasses } from '../ReviewQueue/assuranceAtoms';
import { errorMessage, isMe } from '../ReviewQueue/assuranceShared';
import {
  JOB_FILTERS,
  isActiveJob,
  progressRatio,
  triggerLabel,
  scopeSummary,
  PUBLISHABLE,
} from './recomputeLogic';
import RecomputeJobDetail, { JobStateChip } from './RecomputeJobDetail';
import { RecomputeDialog } from './RecomputeOffer';

const POLL_MS = 5000;

function JobList({ jobs, onOpen, user, canSignOff }) {
  return (
    <div className={`${cardClasses} overflow-hidden`}>
      <table className="w-full text-sm" aria-label="Recompute jobs">
        <thead className="bg-slate-50 hidden md:table-header-group">
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2.5 font-semibold">What changed</th>
            <th className="px-4 py-2.5 font-semibold">Scope</th>
            <th className="px-4 py-2.5 font-semibold">State</th>
            <th className="px-4 py-2.5 font-semibold">Progress</th>
            <th className="px-4 py-2.5 font-semibold">Requested</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {jobs.map((j) => (
            <tr
              key={j.id}
              onClick={() => onOpen(j.id)}
              className="cursor-pointer hover:bg-slate-50 flex flex-col md:table-row px-4 py-3 md:p-0 gap-1"
            >
              <td className="md:px-4 md:py-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(j.id);
                  }}
                  className="font-semibold text-slate-900 text-left hover:underline"
                >
                  {triggerLabel(j.trigger)}
                </button>
              </td>
              <td className="md:px-4 md:py-3 text-slate-600">{scopeSummary(j.scope)}</td>
              <td className="md:px-4 md:py-3">
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <JobStateChip state={j.state} />
                  {PUBLISHABLE.includes(j.state) && (
                    <span className="text-[11px] font-medium text-slate-700">
                      {isMe(user, j.requested_by)
                        ? 'Waiting for a second user'
                        : canSignOff
                          ? 'Ready for your sign-off'
                          : 'Waiting for sign-off'}
                    </span>
                  )}
                </span>
              </td>
              <td className="md:px-4 md:py-3">
                <ProgressBar
                  value={progressRatio(j)}
                  label={`Progress of ${triggerLabel(j.trigger)}`}
                />
              </td>
              <td className="md:px-4 md:py-3 text-xs text-slate-500">
                <span className="text-slate-700">{j.requested_by}</span>
                <span className="block tabular-nums">{fmtDateTime(j.created_at)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const RecomputeJobs = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canRun = hasPermission('lexistar.recompute.run');
  const canSignOff = hasPermission('lexistar.recompute.signoff');
  // Either key opens the page (pagePermissions): .run starts jobs, .signoff
  // publishes them — the second user must reach a job it did not start.
  const canView = canRun || canSignOff;
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = JOB_FILTERS.some((f) => f.value === searchParams.get('show'))
    ? searchParams.get('show')
    : 'all';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [signal, setSignal] = useState(0);
  const pollTimer = useRef(null);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!canView || id) return;
      try {
        if (!quiet) setLoading(true);
        const states = JOB_FILTERS.find((f) => f.value === filter).states;
        const res = await listRecomputeJobs({ state: states.length ? states : undefined });
        const list = pickList(res, 'recompute_jobs', 'jobs');
        setJobs([...list].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
        setError(null);
      } catch (err) {
        if (!quiet) setError(errorMessage(err, 'Failed to load recompute jobs.'));
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [canView, id, filter]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    clearTimeout(pollTimer.current);
    if (!id && jobs.some(isActiveJob))
      pollTimer.current = setTimeout(() => load({ quiet: true }), POLL_MS);
    return () => clearTimeout(pollTimer.current);
  }, [jobs, id, load]);

  const connection = useAssuranceEvents({
    enabled: canView,
    onRecompute: () => {
      setSignal((s) => s + 1);
      load({ quiet: true });
    },
  });

  const header = (
    <PageHeader
      icon={FaHistory}
      title="Recompute Jobs"
      subtitle="History rebuilt as a diff; a second user publishes it."
      toolbar={
        canView && !id ? (
          <SegmentedControl
            options={JOB_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            value={filter}
            onChange={(v) => setSearchParams(v === 'all' ? {} : { show: v }, { replace: true })}
            ariaLabel="Jobs"
          />
        ) : null
      }
      actions={
        canView ? (
          <div className="flex items-center gap-2">
            <ConnectionIndicator status={connection} />
            {!id && (
              <>
                <button
                  type="button"
                  onClick={() => load()}
                  aria-label="Refresh"
                  className="inline-flex items-center px-3 py-2 text-sm text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
                </button>
                {canRun && (
                  <button
                    type="button"
                    onClick={() => setManualOpen(true)}
                    className={buttonClasses.primary}
                  >
                    <FaPlus size={11} /> <span className="hidden sm:inline">New recompute…</span>
                  </button>
                )}
              </>
            )}
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to view recompute jobs."
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {id ? (
          <RecomputeJobDetail id={id} recomputeSignal={signal} />
        ) : (
          <>
            <ErrorBanner message={error} onDismiss={() => setError(null)} />
            {loading && jobs.length === 0 ? (
              <LoadingState label="Loading recompute jobs..." />
            ) : jobs.length === 0 ? (
              <div className={cardClasses}>
                <EmptyState icon={FaHistory} title="No recompute jobs">
                  Saving a geofence or route offers “Recompute history…”; a past-dated declaration
                  queues one automatically. You can also start one here.
                </EmptyState>
              </div>
            ) : (
              <JobList
                jobs={jobs}
                user={user}
                canSignOff={canSignOff}
                onOpen={(jobId) => navigate(`/lexistar/recompute-jobs/${jobId}`)}
              />
            )}
          </>
        )}
      </div>
      <RecomputeDialog
        open={manualOpen}
        trigger={{ object_type: 'manual' }}
        onClose={() => setManualOpen(false)}
        onCreated={() => load({ quiet: true })}
      />
    </div>
  );
};

export default RecomputeJobs;
